import { useCallback, useEffect, useMemo, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { Eye, Pencil, Plus, RefreshCw, Search, Trash2 } from 'lucide-react'
import { DatePicker } from 'modules/permit/components/DatePicker'
import { PageTitle } from 'shared/components/layout/PageTitle'
import { Alert, AlertDescription } from 'shared/ui/alert'
import { Button } from 'shared/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from 'shared/ui/card'
import { Input } from 'shared/ui/input'
import { Label } from 'shared/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from 'shared/ui/select'
import { SearchableSelect } from 'shared/ui/searchable-select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from 'shared/ui/table'
import { usePersistedState } from 'shared/hooks/usePersistedState'
import {
  deletePermitAudit,
  getAuditEmployees,
  getMasterLocations,
  searchPermitAudits,
  type AuditEmployeeOption,
  type PermitAuditItem,
  type PermitAuditMasterOption,
} from '../api/permitAuditService'
import {
  collectAuditPlantTokens,
  collectAuditRoleTokens,
  collectAuditUserIdentityTokens,
  isAuditAreaOwnerRole,
  isAuditSafetyRole,
  matchesAuditIdentity,
  matchesAuditPlant,
  resolveAuditAccessibleLocationIds,
} from '../utils/permissions'

type LayoutState = {
  user?: any
  module?: 'E-PTW' | 'ADMIN'
  site?: {
    id?: string
    name?: string
    location?: string
  }
  menus?: any[]
  roles?: any[]
  tenantName?: string
}

const PAGE_SIZE_OPTIONS = ['10', '20', '50']
const AUDIT_RESULT_OPTIONS = [
  { value: 'ALL', label: 'All Results' },
  { value: 'Conform', label: 'Conform' },
  { value: 'Nonconform', label: 'Nonconform' },
]
const NON_CONFORM_OPTIONS = [
  { value: 'ALL', label: 'All' },
  { value: 'Y', label: 'Can Correct On Site' },
  { value: 'N-Remark', label: 'Corrective Action Completed' },
  { value: 'N-NonRemark', label: 'Corrective Action Pending' },
]
const REVIEW_OPTIONS = [
  { value: 'ALL', label: 'All' },
  { value: 'Y', label: 'Pass' },
  { value: 'N', label: 'Fail' },
  { value: '-', label: 'Pending Review' },
]

const formatDate = (value?: string | null) => {
  if (!value) return '-'
  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value
  return parsed.toLocaleString('th-TH', {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  })
}

const resolveNonConformText = (item: PermitAuditItem) => {
  if (item.nonConformResult === 'Y') {
    return 'สามารถดำเนินการแก้ไขจากหน้างานได้'
  }
  if (item.nonConformResult === 'N') {
    return 'ไม่สามารถดำเนินการแก้ไขจากหน้างานได้'
  }
  return ''
}

const resolveReviewText = (value?: string | null) => {
  if (value === 'Y') return 'Pass'
  if (value === 'N') return 'Fail'
  return '-'
}

const normalizeDateInput = (value: string) => {
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : null
}

const normalizeSelectValue = (value: string) => (value === 'ALL' ? null : value)

type AuditSearchFilters = {
  permitNo: string
  locationFilter: string
  auditBy: string
  stDateTime: string
  expDateTime: string
  auditResult: string
  nonConformResult: string
  reviewResult: string
}

const createDefaultAuditFilters = (): AuditSearchFilters => ({
  permitNo: '',
  locationFilter: 'ALL',
  auditBy: 'ALL',
  stDateTime: '',
  expDateTime: '',
  auditResult: 'ALL',
  nonConformResult: 'ALL',
  reviewResult: 'ALL',
})

export function PermitAuditListPage() {
  const location = useLocation()
  const navigate = useNavigate()
  const state = (location.state as LayoutState | undefined) ?? {}

  const [permitNo, setPermitNo] = usePersistedState('permit-audit-list.permitNo', '')
  const [locationFilter, setLocationFilter] = usePersistedState('permit-audit-list.location', 'ALL')
  const [auditBy, setAuditBy] = usePersistedState('permit-audit-list.auditBy', 'ALL')
  const [stDateTime, setStDateTime] = usePersistedState('permit-audit-list.stDateTime', '')
  const [expDateTime, setExpDateTime] = usePersistedState('permit-audit-list.expDateTime', '')
  const [auditResult, setAuditResult] = usePersistedState('permit-audit-list.auditResult', 'ALL')
  const [nonConformResult, setNonConformResult] = usePersistedState('permit-audit-list.nonConformResult', 'ALL')
  const [reviewResult, setReviewResult] = usePersistedState('permit-audit-list.reviewResult', 'ALL')
  const [appliedFilters, setAppliedFilters] = useState<AuditSearchFilters>(() => ({
    permitNo,
    locationFilter,
    auditBy,
    stDateTime,
    expDateTime,
    auditResult,
    nonConformResult,
    reviewResult,
  }))
  const [items, setItems] = useState<PermitAuditItem[]>([])
  const [locationOptions, setLocationOptions] = useState<PermitAuditMasterOption[]>([])
  const [employeeOptions, setEmployeeOptions] = useState<AuditEmployeeOption[]>([])
  const [error, setError] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [pageSize, setPageSize] = usePersistedState('permit-audit-list.pageSize', 10)
  const [currentPage, setCurrentPage] = usePersistedState('permit-audit-list.currentPage', 1)
  const [totalPage, setTotalPage] = useState(1)
  const [totalRecord, setTotalRecord] = useState(0)
  const [refreshToken, setRefreshToken] = useState(0)
  const roleTokens = useMemo(() => collectAuditRoleTokens(state), [state])
  const userIdentityTokens = useMemo(() => collectAuditUserIdentityTokens(state), [state])
  const plantTokens = useMemo(() => collectAuditPlantTokens(state), [state])
  const canReviewAsSafety = useMemo(() => isAuditSafetyRole(roleTokens), [roleTokens])
  const isAreaOwnerRole = useMemo(() => isAuditAreaOwnerRole(roleTokens), [roleTokens])
  const accessibleLocationIds = useMemo(
    () => resolveAuditAccessibleLocationIds(state.user, state.roles),
    [state.user, state.roles],
  )

  useEffect(() => {
    if (!state.user) {
      navigate('/modules', { replace: true })
    }
  }, [navigate, state.user])

  useEffect(() => {
    let isCancelled = false

    const loadMasters = async () => {
      try {
        const [locations, employees] = await Promise.all([
          getMasterLocations(),
          getAuditEmployees(),
        ])

        if (isCancelled) return
        setLocationOptions(locations)
        setEmployeeOptions(employees)
      } catch (err: any) {
        if (isCancelled) return
        setError(err?.message ?? 'Unable to load audit filter options.')
      }
    }

    void loadMasters()

    return () => {
      isCancelled = true
    }
  }, [])

  const loadData = useCallback(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const result = await searchPermitAudits({
        permitNo: appliedFilters.permitNo.trim() || null,
        locationId: normalizeSelectValue(appliedFilters.locationFilter),
        auditBy: normalizeSelectValue(appliedFilters.auditBy),
        stDateTime: normalizeDateInput(appliedFilters.stDateTime),
        expDateTime: normalizeDateInput(appliedFilters.expDateTime),
        auditResult: normalizeSelectValue(appliedFilters.auditResult),
        nonconformResult: normalizeSelectValue(appliedFilters.nonConformResult),
        reviewResult: normalizeSelectValue(appliedFilters.reviewResult),
        pageIndex: currentPage,
        pageSize,
      })
      setItems(result.items)
      setCurrentPage(result.currentPage)
      setTotalPage(Math.max(1, result.totalPage))
      setTotalRecord(result.totalRecord)
    } catch (err: any) {
      setError(err?.message ?? 'Unable to load permit audit list.')
      setItems([])
      setTotalPage(1)
      setTotalRecord(0)
    } finally {
      setIsLoading(false)
    }
  }, [appliedFilters, currentPage, pageSize, setCurrentPage])

  useEffect(() => {
    void loadData()
  }, [loadData, refreshToken])

  const handleSearch = () => {
    setAppliedFilters({
      permitNo,
      locationFilter,
      auditBy,
      stDateTime,
      expDateTime,
      auditResult,
      nonConformResult,
      reviewResult,
    })
    setCurrentPage(1)
    setRefreshToken((value) => value + 1)
  }

  const handleClear = () => {
    const defaults = createDefaultAuditFilters()
    setPermitNo(defaults.permitNo)
    setLocationFilter(defaults.locationFilter)
    setAuditBy(defaults.auditBy)
    setStDateTime(defaults.stDateTime)
    setExpDateTime(defaults.expDateTime)
    setAuditResult(defaults.auditResult)
    setNonConformResult(defaults.nonConformResult)
    setReviewResult(defaults.reviewResult)
    setAppliedFilters(defaults)
    setCurrentPage(1)
    setPageSize(10)
    setRefreshToken((value) => value + 1)
  }

  const handleCreate = () => {
    navigate('/permit-audit/create', { state })
  }

  const handleEdit = (item: PermitAuditItem) => {
    navigate(`/permit-audit/${item.auditId}`, { state })
  }

  const handleView = (item: PermitAuditItem) => {
    navigate(`/permit-audit/${item.auditId}/view`, { state })
  }

  const handleDelete = async (item: PermitAuditItem) => {
    const confirmed = window.confirm(`Delete permit audit ${item.auditId} for permit ${item.permitNo}?`)
    if (!confirmed) return

    try {
      await deletePermitAudit({ auditId: item.auditId })
      setRefreshToken((value) => value + 1)
    } catch (err: any) {
      setError(err?.message ?? 'Unable to delete permit audit.')
    }
  }

  const startIndex = useMemo(() => {
    if (totalRecord === 0) return 0
    return (currentPage - 1) * pageSize + 1
  }, [currentPage, pageSize, totalRecord])

  const endIndex = useMemo(() => {
    if (totalRecord === 0) return 0
    return Math.min(currentPage * pageSize, totalRecord)
  }, [currentPage, pageSize, totalRecord])

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div className="flex-1">
          <PageTitle
            currentPage="permit-audit"
            compact
            titleOverride="Permit Audit"
            descriptionOverride="Search, review, and maintain migrated permit audit records inside the main E-PTW application."
          />
        </div>
        <Button onClick={handleCreate} className="self-start bg-emerald-600 hover:bg-emerald-700">
          <Plus className="mr-2 h-4 w-4" />
          Create Audit
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Search Criteria</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {error ? (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          ) : null}

          <div className="grid gap-4 lg:grid-cols-12">
            <div className="lg:col-span-3">
              <Label htmlFor="permitNo">Permit No</Label>
              <Input
                id="permitNo"
                value={permitNo}
                onChange={(event) => setPermitNo(event.target.value)}
                placeholder="Search by permit number"
                className="mt-1"
              />
            </div>

            <div className="lg:col-span-6">
              <Label htmlFor="location">Plant / Unit</Label>
              <SearchableSelect
                value={locationFilter}
                onValueChange={setLocationFilter}
                className="mt-1"
                options={[
                  { value: 'ALL', label: 'All Plants / Units' },
                  ...locationOptions.map((option) => ({ value: option.value, label: option.label })),
                ]}
              />
            </div>

            <div className="lg:col-span-3">
              <Label htmlFor="auditBy">Audit By</Label>
              <SearchableSelect
                value={auditBy}
                onValueChange={setAuditBy}
                className="mt-1"
                options={[
                  { value: 'ALL', label: 'All Auditors' },
                  ...employeeOptions.map((option) => ({
                    value: option.empId,
                    label: option.empThName ?? option.fullName ?? option.empId,
                  })),
                ]}
              />
            </div>
          </div>

          <div className="grid gap-4 lg:grid-cols-12">
            <div className="lg:col-span-2">
              <Label htmlFor="stDateTime">Audit Date From</Label>
              <DatePicker
                id="stDateTime"
                value={stDateTime}
                onChange={setStDateTime}
                allowPastDates
                allowFutureDates
                className="mt-1 h-10"
                placeholder="Select date..."
              />
            </div>

            <div className="lg:col-span-2">
              <Label htmlFor="expDateTime">Audit Date To</Label>
              <DatePicker
                id="expDateTime"
                value={expDateTime}
                onChange={setExpDateTime}
                allowPastDates
                allowFutureDates
                className="mt-1 h-10"
                placeholder="Select date..."
              />
            </div>

            <div className="lg:col-span-3">
              <Label htmlFor="auditResult">Audit Result</Label>
              <Select value={auditResult} onValueChange={setAuditResult}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {AUDIT_RESULT_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="lg:col-span-3">
              <Label htmlFor="nonConformResult">Nonconform Status</Label>
              <Select value={nonConformResult} onValueChange={setNonConformResult}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {NON_CONFORM_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="lg:col-span-2">
              <Label htmlFor="reviewResult">Review</Label>
              <Select value={reviewResult} onValueChange={setReviewResult}>
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {REVIEW_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex flex-wrap justify-end gap-2">
            <Button variant="outline" onClick={handleClear}>
              Refresh
            </Button>
            <Button onClick={handleSearch}>
              <Search className="mr-2 h-4 w-4" />
              Search
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle>Permit Audit Records</CardTitle>
          <Button variant="outline" onClick={() => setRefreshToken((value) => value + 1)} disabled={isLoading}>
            <RefreshCw className={`mr-2 h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
            Reload
          </Button>
        </CardHeader>
        <CardContent className="space-y-4">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Permit No</TableHead>
                <TableHead>Description</TableHead>
                <TableHead>Plant / Unit</TableHead>
                <TableHead>Auditor</TableHead>
                <TableHead>Audit Result</TableHead>
                <TableHead>Nonconform</TableHead>
                <TableHead>Review</TableHead>
                <TableHead>Audit Date</TableHead>
                <TableHead className="text-right">Action</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-sm text-gray-500">
                    Loading...
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} className="text-center text-sm text-gray-500">
                    No permit audit records found.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item) => (
                  (() => {
                    const isOwnerDoc = matchesAuditIdentity(item.auditBy, userIdentityTokens)
                    const isOwnerPlant = matchesAuditPlant(item.org ?? item.plant, plantTokens)
                    const itemLocationId = typeof item.org === 'string' ? item.org.trim().toLowerCase() : null
                    const hasAreaOwnerLocationAccess = Boolean(
                      itemLocationId && accessibleLocationIds.has(itemLocationId),
                    )
                    const canEdit =
                      isOwnerDoc || isOwnerPlant || canReviewAsSafety || (isAreaOwnerRole && hasAreaOwnerLocationAccess)
                    const canDelete = isOwnerDoc

                    return (
                      <TableRow key={item.auditId}>
                        <TableCell className="font-medium">{item.permitNo}</TableCell>
                        <TableCell>{item.workDescr ?? '-'}</TableCell>
                        <TableCell>{[item.plant, item.unit].filter(Boolean).join(' : ') || '-'}</TableCell>
                        <TableCell>{item.auditByName ?? item.auditBy ?? '-'}</TableCell>
                        <TableCell>{item.auditResult ?? '-'}</TableCell>
                        <TableCell>{resolveNonConformText(item)}</TableCell>
                        <TableCell>{resolveReviewText(item.reviewResult)}</TableCell>
                        <TableCell>{formatDate(item.auditDate)}</TableCell>
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-2">
                            <Button variant="outline" size="sm" onClick={() => handleView(item)}>
                              <Eye className="h-4 w-4" />
                            </Button>
                            {canEdit ? (
                              <Button size="sm" onClick={() => handleEdit(item)}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                            ) : null}
                            {canDelete ? (
                              <Button variant="destructive" size="sm" onClick={() => handleDelete(item)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            ) : null}
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })()
                ))
              )}
            </TableBody>
          </Table>

          <div className="flex flex-col gap-3 text-sm text-gray-600 md:flex-row md:items-center md:justify-between">
            <div>
              Showing {startIndex} - {endIndex} of {totalRecord} records
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <span>Page size</span>
                <Select
                  value={String(pageSize)}
                  onValueChange={(value) => {
                    setPageSize(Number(value))
                    setCurrentPage(1)
                  }}
                >
                  <SelectTrigger className="h-9 w-24">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PAGE_SIZE_OPTIONS.map((value) => (
                      <SelectItem key={value} value={value}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" disabled={currentPage <= 1} onClick={() => setCurrentPage((value) => value - 1)}>
                  Prev
                </Button>
                <span>
                  Page {currentPage} / {Math.max(1, totalPage)}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  disabled={currentPage >= Math.max(1, totalPage)}
                  onClick={() => setCurrentPage((value) => value + 1)}
                >
                  Next
                </Button>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
